// assets/cart-precheckout-guard.js

(() => {
  /**
   * @typedef {Window & { __cartPrecheckoutGuardInstalled?: boolean }} GuardWindow
   */

  /** @type {GuardWindow} */
  const w = window;

  if (w.__cartPrecheckoutGuardInstalled) return;
  w.__cartPrecheckoutGuardInstalled = true;

  let pendingResubmit = false;

  /** @returns {HTMLFormElement | null} */
  const getForm = () => {
    const el = document.getElementById('cart-form');
    return el instanceof HTMLFormElement ? el : null;
  };

  /** @returns {HTMLElement | null} */
  const getCheckoutButton = () => {
    const el = document.getElementById('checkout');
    return el instanceof HTMLElement ? el : null;
  };

  /**
   * @param {string} tagName
   * @returns {Promise<HTMLElement | null>}
   */
  async function ensureReady(tagName) {
    const el = document.querySelector(tagName);
    if (!(el instanceof HTMLElement)) return null;

    await customElements.whenDefined(tagName);

    const fresh = document.querySelector(tagName);
    return fresh instanceof HTMLElement ? fresh : null;
  }

  /**
   * @param {HTMLElement} component
   * @returns {boolean}
   */
  function validate(component) {
    // @ts-ignore
    if (typeof component.validateBeforeCheckout === 'function') {
      // @ts-ignore
      return Boolean(component.validateBeforeCheckout());
    }
    return true;
  }

  /**
   * @param {HTMLElement} component
   * @returns {Promise<void>}
   */
  async function saveAttributes(component) {
    // @ts-ignore
    if (typeof component.updateCartAttributesBeforeCheckout === 'function') {
      // @ts-ignore
      await component.updateCartAttributesBeforeCheckout();
    }
  }

  /**
   * Intercepts checkout attempts and validates RUT, region and commune.
   * @param {Event} event
   */
  async function guardCheckout(event) {
    if (pendingResubmit) return;

    const isSubmit =
      event.type === 'submit' &&
      event.target instanceof HTMLFormElement &&
      event.target.id === 'cart-form';

    const isCheckoutInteraction = (() => {
      if (!(event.target instanceof Element)) return false;
      return Boolean(event.target.closest('#checkout'));
    })();

    if (!isSubmit && !isCheckoutInteraction) return;

    // Nothing to guard when the component is not on the page (setting off).
    // This check MUST stay synchronous: after an await the event has already
    // been processed and preventDefault() no longer cancels the default action.
    if (!document.querySelector('cart-precheckout')) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    const precheckout = await ensureReady('cart-precheckout');
    if (!precheckout) return;

    if (!validate(precheckout)) return;

    await saveAttributes(precheckout);

    const form = getForm();
    if (!form) return;

    // Build checkout URL with province + city pre-fill
    // form.action points to /cart — params there are NOT forwarded to /checkout.
    // Navigate directly to /checkout so Shopify receives the address params.
    try {
      const provinceCode = localStorage.getItem('cart_shipping_province_code') || '';
      const city = localStorage.getItem('cart_shipping_city') || '';
      if (provinceCode || city) {
        const checkoutUrl = new URL('/checkout', window.location.origin);
        if (provinceCode) checkoutUrl.searchParams.set('checkout[shipping_address][province]', provinceCode);
        if (city) checkoutUrl.searchParams.set('checkout[shipping_address][city]', city);
        checkoutUrl.searchParams.set('checkout[shipping_address][country]', 'Chile');
        window.location.href = checkoutUrl.toString();
        return;
      }
    } catch (_) {
      // localStorage unavailable — fall through to normal form submit
    }

    pendingResubmit = true;
    try {
      const btn = getCheckoutButton();
      if (btn && 'requestSubmit' in form) {
        form.requestSubmit(btn);
      } else {
        form.submit();
      }
    } finally {
      setTimeout(() => {
        pendingResubmit = false;
      }, 0);
    }
  }

  document.addEventListener('pointerdown', guardCheckout, true);
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
})();
